export interface PackagePrice {
  price: number;
  price_ferq: string;
}

export interface PaymentPlan extends PackagePrice {
  paymentTerm: string | null;
  recurring: boolean;
}

interface Cadence {
  flag: string;
  divisor: number;
  label: string;
  paymentTerm: string | null;
  recurring: boolean;
}

const CADENCES: Cadence[] = [
  {
    flag: "is_weekly_selected",
    divisor: 52,
    label: "Weekly",
    paymentTerm: "week",
    recurring: true,
  },
  {
    flag: "is_monthly_selected",
    divisor: 12,
    label: "Monthly",
    paymentTerm: "MONTH",
    recurring: true,
  },
  {
    flag: "is_quarterly_selected",
    divisor: 4,
    label: "Quarterly",
    paymentTerm: "quarter",
    recurring: true,
  },
  {
    flag: "is_semi_annual_selected",
    divisor: 2,
    label: "Semi-Annual",
    paymentTerm: "semi_annually",
    recurring: true,
  },
  {
    flag: "is_yearly_selected",
    divisor: 1,
    label: "Yearly",
    paymentTerm: "year",
    recurring: true,
  },
  {
    flag: "is_one_time_selected",
    divisor: 1,
    label: "One Time",
    paymentTerm: null,
    recurring: false,
  },
  {
    flag: "is_due_on_completion_selected",
    divisor: 1,
    label: "Due on Completion",
    paymentTerm: null,
    recurring: false,
  },
];

const DEFAULT_CADENCE: Cadence = {
  flag: "",
  divisor: 12,
  label: "Monthly",
  paymentTerm: "MONTH",
  recurring: true,
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const cadenceFor = (paymentTerms: any): Cadence =>
  CADENCES.find((entry) => paymentTerms && paymentTerms[entry.flag]) ||
  DEFAULT_CADENCE;

export const getPriceAndFrequency = (
  paymentTerms: any,
  totalAmount: any
): PackagePrice => {
  const amount = Number(totalAmount) || 0;

  if (!paymentTerms) {
    return {
      price: Math.ceil(amount / DEFAULT_CADENCE.divisor),
      price_ferq: DEFAULT_CADENCE.label,
    };
  }

  const cadence = cadenceFor(paymentTerms);

  return {
    price: amount / cadence.divisor,
    price_ferq: cadence.label,
  };
};

export const getPaymentPlan = (
  paymentTerms: any,
  totalAmount: any
): PaymentPlan => {
  const cadence = cadenceFor(paymentTerms);
  const { price, price_ferq } = getPriceAndFrequency(paymentTerms, totalAmount);

  return {
    price,
    price_ferq,
    paymentTerm: cadence.paymentTerm,
    recurring: cadence.recurring,
  };
};

// The roadmap entry already knows which cadence the client picked, so it is resolved by
// flag instead of by "first one selected on the package".
export const getPaymentPlanForFlag = (
  flag: string,
  totalAmount: any
): PaymentPlan => {
  const cadence = CADENCES.find((entry) => entry.flag === flag) || DEFAULT_CADENCE;
  const amount = Number(totalAmount) || 0;

  return {
    price: amount / cadence.divisor,
    price_ferq: cadence.label,
    paymentTerm: cadence.paymentTerm,
    recurring: cadence.recurring,
  };
};

// A package the firm never customised carries a contract total and no cadence at all.
// Stating the total mirrors the Scope Agreement, which falls back to the same label,
// instead of inventing an instalment the Tax Pro never offered.
export const TOTAL_LABEL = "Total";

export const getPackageCardPrice = (
  paymentTerms: any,
  totalAmount: any
): PackagePrice =>
  paymentTerms
    ? getPriceAndFrequency(paymentTerms, totalAmount)
    : { price: Number(totalAmount) || 0, price_ferq: TOTAL_LABEL };

export const paymentTermForFlag = (flag: string): string | null => {
  const cadence = CADENCES.find((entry) => entry.flag === flag);
  return cadence ? cadence.paymentTerm : null;
};

export const formatPrice = (price: number): string =>
  currencyFormatter.format(Number(price) || 0);